// src/components/marketing/OptimizerAlertsPage.tsx
// Track E4 — optimizer alerts inbox (A11).
//
// Top: "Nuevo ciclo" button triggers `POST /optimizer` → backend fans
// out per-campaign workers and their output lands here grouped by
// cycle_id. List below shows alerts with category badge, confidence,
// reasoning, and apply/dismiss actions. Applying marks intent —
// executing the action (pause adset, adjust budget) is still a manual
// step via the campaigns module.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Ban, Check, ChevronDown, ChevronRight,
  Gauge, Loader2, PlayCircle, RefreshCw,
} from 'lucide-react';

import {
  ApiError, applyAlert, dismissAlert, listAlerts, triggerOptimizer,
  type OptimizationCategory, type OptimizationLog, type Paginated,
} from '@/lib/api_ai_marketing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props { addNotification?: Notifier; }

const CATEGORY_LABELS: Record<OptimizationCategory, string> = {
  budget: 'Presupuesto',
  pause_adset: 'Pausar adset',
  rotate_creative: 'Rotar creativo',
  change_objective: 'Cambiar objetivo',
  extend_duration: 'Extender duración',
  new_audience: 'Nueva audiencia',
  other: 'Otro',
};

const CATEGORY_COLORS: Record<OptimizationCategory, string> = {
  budget: 'bg-emerald-500/15 text-emerald-300',
  pause_adset: 'bg-red-500/15 text-red-300',
  rotate_creative: 'bg-purple-500/15 text-purple-300',
  change_objective: 'bg-sky-500/15 text-sky-300',
  extend_duration: 'bg-amber-500/15 text-amber-300',
  new_audience: 'bg-pink-500/15 text-pink-300',
  other: 'bg-white/10 text-white/70',
};

export function OptimizerAlertsPage({ addNotification }: Props) {
  const [list, setList] = useState<Paginated<OptimizationLog> | null>(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [filter, setFilter] = useState<{
    applied?: boolean; dismissed?: boolean;
    category?: OptimizationCategory;
  }>({ dismissed: false });
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());
  const [pending, setPending] = useState<Record<number, 'apply' | 'dismiss'>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listAlerts({ ...filter, limit: 50 });
      setList(rows);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
      addNotification?.({ type: 'error', title: 'No se pudieron cargar las alertas', message: msg });
    } finally {
      setLoading(false);
    }
  }, [filter, addNotification]);

  useEffect(() => { load(); }, [load]);

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      const out = await triggerOptimizer();
      addNotification?.({
        type: 'success', title: 'Ciclo encolado',
        message: out.detail || 'Las sugerencias aparecen conforme los workers terminan.',
        duration: 4000,
      });
      // Poll once after a short delay so the first results appear without
      // forcing the user to hit refresh.
      window.setTimeout(load, 3000);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
      addNotification?.({ type: 'error', title: 'No se pudo iniciar', message: msg });
    } finally {
      setTriggering(false);
    }
  };

  const handleApply = async (row: OptimizationLog) => {
    setPending((p) => ({ ...p, [row.id]: 'apply' }));
    try {
      const updated = await applyAlert(row.id);
      setList((prev) => prev ? { ...prev, items: prev.items.map((r) => r.id === updated.id ? updated : r) } : prev);
      addNotification?.({
        type: 'success',
        title: 'Marcada como aplicada',
        message: 'Ejecuta la acción desde el detalle de la campaña.',
      });
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
      addNotification?.({ type: 'error', title: 'Error', message: msg });
    } finally {
      setPending((p) => { const { [row.id]: _d, ...rest } = p; return rest; });
    }
  };

  const handleDismiss = async (row: OptimizationLog) => {
    setPending((p) => ({ ...p, [row.id]: 'dismiss' }));
    try {
      const updated = await dismissAlert(row.id);
      setList((prev) => prev ? { ...prev, items: prev.items.map((r) => r.id === updated.id ? updated : r) } : prev);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error');
      addNotification?.({ type: 'error', title: 'Error', message: msg });
    } finally {
      setPending((p) => { const { [row.id]: _d, ...rest } = p; return rest; });
    }
  };

  // Group the flat list by cycle_id so the UI shows "ciclo de tal fecha:
  // N sugerencias" blocks.
  const groups = useMemo(() => {
    if (!list) return [];
    const by: Map<string, OptimizationLog[]> = new Map();
    for (const r of list.items) {
      const arr = by.get(r.cycle_id) ?? [];
      arr.push(r);
      by.set(r.cycle_id, arr);
    }
    return Array.from(by.entries()).map(([cycleId, items]) => ({
      cycleId,
      when: items[0]?.created_at,
      items,
    }));
  }, [list]);

  return (
    <div className="space-y-5">
      <div className="o-card p-5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Gauge size={18} className="text-[var(--acc1,#00e5ff)]" />
            Optimizer · alertas
          </h1>
          <div className="flex-1" />
          <button
            type="button"
            onClick={handleTrigger}
            disabled={triggering}
            className="px-3 py-1.5 rounded-lg bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 flex items-center gap-1 text-sm font-medium disabled:opacity-50"
          >
            {triggering ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            {triggering ? 'Encolando…' : 'Nuevo ciclo'}
          </button>
        </div>
        <p className="text-sm text-white/60 mt-2">
          El beat corre cada 4 h sobre campañas activas de la org. Usa este botón
          para pedir un análisis ad-hoc. Los resultados aparecen abajo conforme
          los workers terminan — no se ejecuta ninguna acción automáticamente.
        </p>
      </div>

      <div className="o-card p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h2 className="text-base font-semibold">Sugerencias</h2>
          <div className="flex-1" />
          <FilterPill active={filter.dismissed === false && !filter.applied} onClick={() => setFilter({ dismissed: false })}>
            Activas
          </FilterPill>
          <FilterPill active={filter.applied === true} onClick={() => setFilter({ applied: true })}>
            Aplicadas
          </FilterPill>
          <FilterPill active={filter.dismissed === true} onClick={() => setFilter({ dismissed: true })}>
            Descartadas
          </FilterPill>
          <FilterPill active={Object.keys(filter).length === 0} onClick={() => setFilter({})}>
            Todas
          </FilterPill>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="o-btn-ghost p-1.5"
            title="Refrescar"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading && !list ? (
          <div className="py-10 text-center opacity-60">
            <Loader2 className="inline animate-spin" size={18} />
          </div>
        ) : !list || list.items.length === 0 ? (
          <div className="py-10 text-center text-sm text-white/50">
            Sin alertas. Ejecuta <strong>Nuevo ciclo</strong> para pedirle al LLM que analice.
          </div>
        ) : (
          <div className="space-y-5">
            {groups.map((g) => (
              <div key={g.cycleId}>
                <div className="text-xs uppercase tracking-wider text-white/40 mb-2">
                  Ciclo del {formatDate(g.when)} · {g.items.length} sugerencia{g.items.length === 1 ? '' : 's'}
                </div>
                <div className="space-y-2">
                  {g.items.map((row) => (
                    <AlertCard
                      key={row.id}
                      row={row}
                      expanded={openIds.has(row.id)}
                      onToggle={() => setOpenIds((s) => {
                        const next = new Set(s);
                        if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
                        return next;
                      })}
                      pending={pending[row.id]}
                      onApply={() => handleApply(row)}
                      onDismiss={() => handleDismiss(row)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Card
// ═══════════════════════════════════════════════════════════════

function AlertCard({
  row, expanded, onToggle, pending, onApply, onDismiss,
}: {
  row: OptimizationLog;
  expanded: boolean;
  onToggle: () => void;
  pending?: 'apply' | 'dismiss';
  onApply: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className={`rounded-lg border overflow-hidden ${
      expanded ? 'border-[var(--acc1,#00e5ff)]/30 bg-[var(--acc1,#00e5ff)]/5' : 'border-white/10 bg-white/5'
    }`}>
      <button type="button" onClick={onToggle} className="w-full text-left px-4 py-3">
        <div className="flex items-center gap-2 text-sm min-w-0">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <CategoryBadge category={row.category} />
          <span className="truncate flex-1">{row.suggestion_text}</span>
          {row.confidence !== null && <ConfidenceBar value={row.confidence} />}
          {row.applied && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-emerald-500/15 text-emerald-300">
              ✓ Aplicada
            </span>
          )}
          {row.dismissed && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-white/10 text-white/60">
              Descartada
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {row.reasoning && (
            <>
              <div className="text-xs uppercase tracking-wider text-white/40">Razonamiento</div>
              <div className="text-sm bg-white/5 p-3 rounded-md whitespace-pre-wrap">{row.reasoning}</div>
            </>
          )}

          {Object.keys(row.action_payload).length > 0 && (
            <>
              <div className="text-xs uppercase tracking-wider text-white/40">Acción sugerida</div>
              <pre className="text-xs bg-white/5 p-3 rounded-md overflow-x-auto font-mono">
                {JSON.stringify(row.action_payload, null, 2)}
              </pre>
            </>
          )}

          <div className="flex items-center gap-2 text-xs text-white/40">
            <span>Campaña #{row.campaign_id}</span>
            {row.provider && <span>· {row.provider} · {row.model}</span>}
            <div className="flex-1" />
            <span>{formatDate(row.created_at)}</span>
          </div>

          {!row.applied && !row.dismissed && (
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onDismiss}
                disabled={!!pending}
                className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 text-sm flex items-center gap-1"
              >
                {pending === 'dismiss' ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                Descartar
              </button>
              <button
                type="button"
                onClick={onApply}
                disabled={!!pending}
                className="px-3 py-1.5 rounded-md bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-sm font-medium flex items-center gap-1"
              >
                {pending === 'apply' ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Marcar aplicada
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryBadge({ category }: { category: OptimizationCategory }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium ${CATEGORY_COLORS[category]}`}>
      {CATEGORY_LABELS[category]}
    </span>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  const color = pct >= 75 ? 'bg-emerald-400' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-1 shrink-0" title={`Confianza ${pct}%`}>
      <div className="w-14 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-white/50">{pct}%</span>
    </div>
  );
}

function FilterPill({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded-md text-xs ${
        active
          ? 'bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)]'
          : 'bg-white/5 text-white/60 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default OptimizerAlertsPage;
